import React from "react";
import { useNavigate } from "react-router-dom";
import { Reward } from "../types/reward.entity";
import { FaPen } from "react-icons/fa";
import { getRewardProviderMeta } from "../utils/provider-meta";
import { getRewardPayoutLabel } from "../utils/reward-display";

interface RewardCardProps {
  reward: Reward;
  onEdit?: (reward: Reward) => void;
}

const RewardCard: React.FC<RewardCardProps> = ({ reward, onEdit }) => {
  const provider = getRewardProviderMeta(reward.provider.name);
  const payoutLabel = getRewardPayoutLabel(reward);
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/rewards/${reward.id}`)}
      className="group bg-white rounded-xl overflow-hidden transition-shadow duration-200 hover:shadow-md border border-gray-200 flex flex-col h-full cursor-pointer"
    >
      {/* Image */}
      <div className="relative h-44 w-full overflow-hidden">
        {reward.attachment?.fileUrl ? (
          <img
            src={reward.attachment.fileUrl}
            alt={reward.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${provider.gradient}`}>
            <span className="text-white/20 text-7xl">{provider.icon}</span>
          </div>
        )}

        {/* Availability dot */}
        <div className="absolute top-3 right-3">
          <span
            className={`block w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-sm ${reward.isAvailable ? "bg-emerald-400" : "bg-red-400"
              }`}
            title={reward.isAvailable ? "Available" : "Unavailable"}
          />
        </div>
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col gap-2">
        {/* Provider */}
        <span className={`text-xs font-medium ${provider.color}`}>
          {provider.label}
        </span>

        {/* Name */}
        <h3
          className="text-sm font-semibold text-gray-900 line-clamp-1 group-hover:text-brand-500 transition-colors"
          title={reward.name}
        >
          {reward.name}
        </h3>

        {payoutLabel && (
          <p className="text-xs text-gray-400 font-medium">
            Payout {payoutLabel}
          </p>
        )}

        {/* Karma cost */}
        <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-brand-500">
            <img src="/karma-icon.png" alt="Karma" className="w-6 h-6 object-contain" />
            <span className="text-base font-bold tabular-nums">
              {reward.amount?.toLocaleString() || 0}
            </span>
          </div>

          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(reward);
              }}
              className="p-1.5 rounded-md text-gray-400 hover:text-brand-500 hover:bg-brand-50 transition-colors cursor-pointer"
              title="Edit reward"
            >
              <FaPen className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardCard;
